import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { Router } from '@angular/router';
import { Profile } from '@shared/entities/profile';
import { ProfileService } from '@shared/services/api/profile.service';
import { AuthenticationService } from '@shared/services/authentication.service';
import { first, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../services/theme.service';
import { MachineAlertService } from '@shared/services/api/machine-alert.service';
import { fadeInAnimation } from '@shared/animations';
import { CompanyGroupUsersService } from '@shared/services/api/company-group-users.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation]
})
export class HeaderComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _authenticationService = inject(AuthenticationService);
  private _profileService = inject(ProfileService);
  private _machineAlertService = inject(MachineAlertService);
  private _companyGroupUsersService = inject(CompanyGroupUsersService);
  private _router = inject(Router);

  @Input() title!: string;
  @Input() subtitle!: string;

  isLight?: boolean;
  dropdownOpen = false;
  brokenMachinesCount = 0;

  profile!: Profile;
  userImage?: string | null;
  companyGroupName = '';

  get isImpersonating(): boolean {
    return !!localStorage.getItem('originalUserEmail');
  }

  get originalUserEmail(): string | null {
    return localStorage.getItem('originalUserEmail');
  }

  constructor() {
    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  ngOnInit() {
    this._profileService.profile$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((profile) => {
          this.profile = profile;
          this.userImage = profile.photo;
          this.companyGroupName = profile.companyGroupName || '';
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._machineAlertService.broken$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((machines) => {
          this.brokenMachinesCount = machines.length;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
    this._cdr.markForCheck();
  }

  onLogout() {
    this._authenticationService
      .logout()
      .pipe(first())
      .subscribe(() => {
        this._router.navigate(['/login']);
      });
  }

  onResetImpersonification() {
    const originalUserEmail = this.originalUserEmail;
    if (!originalUserEmail) return;

    this._companyGroupUsersService.resetUser(originalUserEmail).subscribe({
      next: (response) => {
        localStorage.removeItem('originalUserEmail');
        this._authenticationService.signInWithCustomToken(response.customToken).subscribe({
          next: () => {
            this._router.navigate(['/']).then(() => window.location.reload());
          },
          error: () => { /* noop */ }
        });
      },
      error: () => { /* noop */ }
    });
  }
}
